<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          Covering requests
        </oxd-text>

        <oxd-switch-input
          v-model="showResponded"
          option-label="Show responded"
          label-position="left"
        />
      </div>

      <div class="orangehrm-container">
        <oxd-card-table
          :headers="headers"
          :items="visibleCoverRequests"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>

      <div
        v-if="visibleCoverRequests.length === 0 && !isLoading"
        class="orangehrm-bottom-container"
      >
        <div class="orangehrm-empty-container">
          <p>No cover requests found</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {ref, onMounted, h, computed} from 'vue';
import {OxdSwitchInput} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';
import usei18n from '@/core/util/composable/usei18n';
import useToast from '@/core/util/composable/useToast';

export default {
  name: 'CoverRequests',
  components: {
    'oxd-switch-input': OxdSwitchInput,
  },
  setup() {
    const {$t} = usei18n();
    const {updateSuccess} = useToast();
    const apiService = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/cover-requests',
    );

    const isLoading = ref(false);
    const coverRequests = ref([]);
    const isProcessing = ref(false);
    const showResponded = ref(true);

    const visibleCoverRequests = computed(() => {
      if (showResponded.value) {
        return coverRequests.value;
      }
      return coverRequests.value.filter((item) => !item.isDisabled);
    });

    const TextCell = {
      name: 'CoverRequestsTextCell',
      props: {
        value: {type: [String, Number], required: false, default: ''},
        disabled: {type: Boolean, required: false, default: false},
      },
      setup(props) {
        return () =>
          h(
            'span',
            {
              class: [
                'cover-requests-cell',
                props.disabled ? 'cover-requests-cell--disabled' : null,
              ],
            },
            String(props.value ?? ''),
          );
      },
    };

    const cellRenderer = (index, value, header, rowItem) => {
      return {
        component: TextCell,
        props: {
          value,
          disabled: Boolean(rowItem?.isDisabled),
        },
      };
    };

    const getAcceptLabel = () => {
      const maybeAccept = $t('general.accept');
      return maybeAccept === 'general.accept'
        ? $t('general.approve')
        : maybeAccept;
    };

    const formatEmployeeName = (employee) => {
      if (!employee) return '';
      const name = `${employee.firstName || ''} ${employee.middleName || ''} ${
        employee.lastName || ''
      }`.replace(/\s+/g, ' ');
      return name.trim();
    };

    const processCoverAction = (leaveRequestId, action) => {
      if (isProcessing.value) return;
      isProcessing.value = true;
      const coverActionApi = new APIService(
        window.appGlobal.baseUrl,
        `/api/v2/leave/leave-requests/${leaveRequestId}/cover-action`,
      );

      coverActionApi
        .request({method: 'PUT', data: {action}})
        .then(() => {
          updateSuccess();
          fetchCoverRequests();
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error processing cover request:', error);
        })
        .finally(() => {
          isProcessing.value = false;
        });
    };

    const headers = [
      {
        name: 'employee',
        slot: 'title',
        title: $t('general.employee_name'),
        style: {flex: 1},
        cellRenderer,
      },
      {
        name: 'leaveType',
        title: $t('leave.leave_type'),
        style: {flex: 1},
        cellRenderer,
      },
      {
        name: 'fromDate',
        title: $t('general.from_date'),
        style: {flex: 1},
        cellRenderer,
      },
      {
        name: 'toDate',
        title: $t('general.to_date'),
        style: {flex: 1},
        cellRenderer,
      },
      {
        name: 'numberOfDays',
        title: $t('leave.number_of_days'),
        style: {flex: 0.5},
        cellRenderer,
      },
      {
        name: 'coverStatus',
        title: $t('general.status'),
        style: {flex: 0.75},
        cellRenderer,
      },
      {
        name: 'actions',
        slot: 'footer',
        title: $t('general.actions'),
        style: {flex: 1},
        cellType: 'oxd-table-cell-actions',
        cellConfig: {
          accept: {
            onClick: (item) => processCoverAction(item.id, 'accept'),
            component: 'oxd-button',
            props: {
              label: getAcceptLabel(),
              displayType: 'secondary',
              size: 'small',
            },
          },
          reject: {
            onClick: (item) => processCoverAction(item.id, 'reject'),
            component: 'oxd-button',
            props: {
              label: $t('general.reject'),
              displayType: 'ghost',
              size: 'small',
            },
          },
        },
      },
    ];

    const fetchCoverRequests = () => {
      isLoading.value = true;
      apiService
        // Avoid stale ETag/localStorage cached responses across users/sessions.
        .getAll({_: Date.now()})
        .then((response) => {
          const raw = response.data.data || [];
          const mapped = raw.map((item) => {
            const employeeName = formatEmployeeName(item.employee);
            const status = item.coverAssignment?.status || 'PENDING';
            const statusLabel = String(status)
              .toLowerCase()
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase());
            const allowedActions = item.allowedActions || [];
            const canAccept = allowedActions.some(
              (a) => a.action === 'ACCEPT COVER',
            );
            const canReject = allowedActions.some(
              (a) => a.action === 'REJECT COVER',
            );

            return {
              id: item.id,
              employee: employeeName,
              leaveType: item.leaveType?.name,
              fromDate: item.dates?.fromDate,
              toDate: item.dates?.toDate,
              numberOfDays: item.noOfDays,
              coverStatus: statusLabel,
              isDisabled: !canAccept && !canReject,
            };
          });

          // Show pending requests first, then responded.
          mapped.sort((a, b) => {
            if (a.isDisabled === b.isDisabled) return 0;
            return a.isDisabled ? 1 : -1;
          });

          coverRequests.value = mapped;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error fetching cover requests:', error);
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    onMounted(() => {
      fetchCoverRequests();
    });

    return {
      isLoading,
      coverRequests,
      showResponded,
      visibleCoverRequests,
      headers,
    };
  },
};
</script>

<style scoped>
.cover-requests-cell--disabled {
  opacity: 0.55;
}

.orangehrm-empty-container {
  text-align: center;
  padding: 2rem;
  color: var(--oxd-text-input-control-label-font-color);
}
</style>
